/**
 * ResultViewPage — the walk-in result-view preview (PP-3). Enter a demande id → GET /portal/resultats/:demandeId
 * → the verbatim CompteRendu snapshot (libelle/valeur/unite/drapeau + the FROZEN intervalle_reference,
 * NEVER recomputed) as the patient would see it, plus a link to the stored bilingual PDF. Only `emis`
 * (valide-derived) reports surface (D-15) — a demande without one yields a calm "no report yet" empty
 * state.
 *
 * Cloud-only: when the module is absent the result-view route 404s with the generic "Route introuvable",
 * which we tell apart from the data 404 ("Aucun compte-rendu émis.") and render the "indisponible"
 * panel. Full 5-state DoD; sans-droit is this screen's own ForbiddenState on dossier.lire.
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { token } from "@/design/theme";
import { PP_CAPS, isResultViewModuleAbsent, useResultView } from "../api";
import type { ResultLine, ResultViewResponse } from "../types";
import { PageHeader } from "../components/PageHeader";
import { ModuleAbsentState } from "../components/ModuleAbsentState";
import { LookupForm } from "../components/LookupForm";
import { FlagBadge } from "../components/FlagBadge";

export function ResultViewPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(PP_CAPS.dossierLire);

  const [demandeId, setDemandeId] = useState("");
  const view = useResultView(demandeId, canRead && online);

  if (!canRead) return <ForbiddenState requiredCapabilities={[PP_CAPS.dossierLire]} />;

  const header = (
    <PageHeader title={t("pp.result.title")} subtitle={t("pp.result.subtitle")} />
  );

  // Cloud-only degradation: the module is absent (generic "Route introuvable"), not a data 404.
  if (isResultViewModuleAbsent(view.error)) {
    return (
      <Screen>
        {header}
        <ModuleAbsentState />
      </Screen>
    );
  }

  // A data 404 (module mounted, but no emis report yet) — the legitimate vide for this demande.
  const noReport = !!view.error && view.error.httpStatus === 404;

  return (
    <Screen>
      {header}

      <LookupForm
        label={t("pp.result.field.demandeId")}
        hint={t("pp.result.field.demandeId.hint")}
        submitLabel={t("pp.result.lookup")}
        loading={view.isFetching}
        onSubmit={setDemandeId}
      />

      {demandeId === "" ? null : !online && view.data === undefined ? (
        <OfflineState onRetry={() => void view.refetch()} cached={false} />
      ) : view.isLoading ? (
        <LoadingState variant="table" />
      ) : noReport ? (
        <EmptyState glyph="▢" title={t("pp.result.none.title")} description={t("pp.result.none.desc")} />
      ) : view.isError ? (
        <ErrorState
          message={view.error?.message ?? ""}
          {...(view.error?.code ? { code: view.error.code } : {})}
          onRetry={() => void view.refetch()}
        />
      ) : view.data ? (
        <ReportView report={view.data} />
      ) : null}
    </Screen>
  );
}

function ReportView({ report }: { report: ResultViewResponse }) {
  const { t } = useT();

  const columns: ReadonlyArray<Column<ResultLine>> = [
    {
      key: "analyse",
      header: t("pp.result.col.analyse"),
      cell: (l) => <span style={{ fontWeight: 600 }}>{l.analyse}</span>,
    },
    {
      key: "valeur",
      header: t("pp.result.col.valeur"),
      mono: true,
      align: "end",
      cell: (l) => l.valeur ?? t("pp.na"),
    },
    {
      key: "unite",
      header: t("pp.result.col.unite"),
      mono: true,
      cell: (l) => l.unite ?? t("pp.na"),
    },
    {
      key: "reference",
      header: t("pp.result.col.reference"),
      mono: true,
      cell: (l) => l.intervalle_reference ?? t("pp.na"),
    },
    {
      key: "drapeau",
      header: t("pp.result.col.drapeau"),
      cell: (l) => <FlagBadge drapeau={l.drapeau} />,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: token.s4,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: token.s4, flexWrap: "wrap", alignItems: "center" }}>
            <Meta label={t("pp.result.meta.numero")} value={report.numero} />
            <Meta label={t("pp.result.meta.version")} value={`v${report.version}`} />
          </div>
          {report.pdf_url ? (
            <Button
              variant="secondary"
              leadingIcon={<span aria-hidden>⤓</span>}
              onClick={() => window.open(report.pdf_url ?? "", "_blank", "noopener,noreferrer")}
            >
              {t("pp.result.pdf")}
            </Button>
          ) : (
            <Badge tone="neutral" glyph="⌀">
              {t("pp.result.pdf.absent")}
            </Badge>
          )}
        </div>
      </Card>

      <Card title={t("pp.result.title")} padded={false}>
        <Table<ResultLine>
          columns={columns}
          rows={report.lignes}
          rowKey={(l, i) => `${l.analyse}-${i}`}
          emptyTitle={t("pp.result.lignes.empty.title")}
          emptyDescription={t("pp.result.lignes.empty.desc")}
          caption={t("pp.result.title")}
        />
      </Card>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ fontSize: token.fsLabel, color: token.slate }}>{label}</span>
      <span className="nv-mono nv-ltr-island" style={{ fontWeight: 700, color: token.ink }}>
        {value}
      </span>
    </span>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
